'use client';
import { Button, Empty, message, notification, Space, Spin } from 'antd';
import BannerCardComp from './bannerCard/bannerCard.index';
import styles from './home.module.scss';
import { AppLinkComp, PostCardComp } from 'components';
import { PaginationType, PostType } from 'types';
import { PATH } from 'data';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { GlobalContext } from 'context';
import { isAppInstalled, useBP, useRealTimeState, useThen, AppStorage } from 'scripts';
import { TbListDetails } from 'react-icons/tb';
import { InView } from 'react-intersection-observer';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { __FetchHomePosts, __HomeLoadedPage, __HomePageQueryKey } from './home.script';

export default function HomeComp(props: { posts: PaginationType<PostType<'list'>>; ads: PostType<'list'>[] }) {
    // const [posts, setPosts] = useState<PaginationType<PostType<'list'>> | undefined>(props.posts);
    const { locations } = useContext(GlobalContext);
    // page should be exclude from query base
    const queryBase = useMemo(() => ({ locations: locations?.map((i) => i.id) ?? [] }), [locations]);
    const queryKey = useMemo(() => ['posts', 'landing', JSON.stringify(queryBase)], [queryBase]);
    const pageQueryKey = useMemo(() => __HomePageQueryKey(queryKey), [queryKey]);
    const queryClient = useQueryClient();
    const [page, setPageState] = useState(
        () =>
            queryClient.getQueryData<number>(pageQueryKey) ??
            __HomeLoadedPage(queryClient.getQueryData<PaginationType<PostType<'list'>>>(queryKey)) ??
            1,
    );
    const prevQueryKeyRef = useRef(queryKey);
    if (prevQueryKeyRef.current !== queryKey) {
        prevQueryKeyRef.current = queryKey;
        const nextPage = queryClient.getQueryData<number>(pageQueryKey) ?? 1;
        if (page !== nextPage) setPageState(nextPage);
    }
    const setPage = useCallback(
        (value: React.SetStateAction<number>) => {
            setPageState((prev) => {
                const next = typeof value === 'function' ? value(prev) : value;
                queryClient.setQueryData(pageQueryKey, next);
                return next;
            });
        },
        [queryClient, pageQueryKey],
    );
    const query = useQuery({
        queryKey,
        queryFn: () => {
            const prev = page > 1 ? queryClient.getQueryData<PaginationType<PostType<'list'>>>(queryKey) : undefined;
            return __FetchHomePosts({ page, locations }, prev);
        },
        initialData: props.posts,
        staleTime: 30 * 60 * 1000,
    });
    const isMobile = useBP()?.MD_LTE;
    const loadMore = useRealTimeState(false);
    const downloadCancelTime = AppStorage.appDownloadTime;

    useEffect(() => {
        if (!isAppInstalled() && downloadCancelTime.get()) {
            const key = `home-download-notification`;
            const btn = (
                <Space>
                    <Button type="link" onClick={() => notification.destroy(key)}>
                        لغو
                    </Button>
                    <Button type="primary" href={PATH.download} target="_blank">
                        دانلود
                    </Button>
                </Space>
            );
            notification.open({
                message: 'دانلود اپلیکیشن تیک سبز',
                description: 'برای دسترسی و استفاده بهتر، اپلیکیشن تیک سبز را دانلود کنید.',
                btn,
                key,
                onClose: () => {
                    downloadCancelTime.set();
                    notification.destroy(key);
                },
            });
        }
    }, []);

    useThen(() => {
        queryClient
            .cancelQueries({ queryKey })
            .then(() => {
                if (page === 1) {
                    queryClient.setQueryData(queryKey, undefined);
                    queryClient.setQueryData(pageQueryKey, 1);
                } else loadMore.set(true);
                return query.refetch();
            })
            .catch(() => {
                message.error('دریافت آگهی‌ها با خطا مواجه شد. لطفا بعدا تلاش کنید.');
                window.scrollTo({ top: 0, behavior: 'smooth' });
            })
            .finally(() => loadMore.set(false));
    }, [locations, page]);

    const posts = query.data;

    return (
        <div className={`${styles['home-layout']}`}>
            {props.ads.length >= 2 ? (
                <div className={`${styles['banner-layout']} d-flex mb-7`}>
                    {props.ads.slice(0, 2).map((item, index) => (
                        <BannerCardComp data={item} key={index} />
                    ))}
                </div>
            ) : null}
            <div className="center-content-y justify-content-between mb-5 g-3">
                <h2 className={`t-h2 t-500 t-l-black1 ${styles['title']}`}>
                    جدیدترین آگهی{locations?.length ? ' های ' + locations.map((i) => i.title).join(' و ') : ' ها'}
                </h2>
                <AppLinkComp href={PATH.posts()}>
                    <Button type="primary" icon={isMobile ? <TbListDetails /> : null}>
                        {isMobile ? null : 'مشاهده بیشتر'}
                    </Button>
                </AppLinkComp>
            </div>
            {posts ? (
                posts.count > 0 ? (
                    <>
                        <div className={`${styles['ads-layout']}`}>
                            {posts.data.map((post, index) => (
                                <React.Fragment key={index}>
                                    {posts.num_of_pages !== page && index >= posts.data.length - 12 ? (
                                        <InView
                                            as="div"
                                            onChange={(inView) => {
                                                if (inView && !loadMore.get()) setPage(page + 1);
                                            }}
                                        >
                                            <PostCardComp data={post} />
                                        </InView>
                                    ) : (
                                        <PostCardComp data={post} />
                                    )}
                                </React.Fragment>
                            ))}
                            {Array.from({ length: 3 }).map((_, index) => (
                                <div key={index} />
                            ))}
                        </div>
                        {posts.num_of_pages !== page ? (
                            <InView
                                as="div"
                                className="center-content pv-2 w-100"
                                onChange={(inView) => {
                                    if (inView && !loadMore.get()) setPage(page + 1);
                                }}
                            >
                                <Spin />
                            </InView>
                        ) : null}
                    </>
                ) : (
                    <Empty
                        className="pv-6 flex-1 center-content flex-column"
                        description={
                            'آگهی ای یافت نشد. ' +
                            (locations
                                ? 'برای جستجوی گسترده‌تر موقعیت مکانی مورد نظر خود را تغییر دهید.'
                                : 'لطفا بعدا مراجعه کنید.')
                        }
                    />
                )
            ) : (
                <div className={`w-100 pv-9 center-content flex-1`}>
                    <Spin />
                </div>
            )}
        </div>
    );
}
